import React, { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom';
import ClientDetaileNavBar from './ClientDetaileNavBar'
import LiveSessionsService from '../Api/LiveSessionsService';
import AddonPacksService from '../Api/AddonPacksService';
import CourseService from '../Api/CourseService';
import Swal from 'sweetalert2/dist/sweetalert2.js';

function ClientDetaileLayout() {
    const navigate = useNavigate();
    const [liveSession, setLiveSession] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);
    const [showJoin, setShowJoin] = useState(false);
    const liveSessionsService = new LiveSessionsService();
    const addonPacksService = new AddonPacksService();
    const courseService = new CourseService();

    useEffect(() => {
        const fetchLiveSessions = async () => {
            try {
                const response = await liveSessionsService.GetLiveSessions();
                if (response?.data?.data) {
                    const now = new Date();
                    const sessions = response.data.data;

                    // Sort by session_datetime_ist ascending
                    sessions.sort((a, b) => new Date(a.session_datetime_ist) - new Date(b.session_datetime_ist));

                    // Find the first session that is ongoing or upcoming
                    const nextSession = sessions.find(session => {
                        const start = new Date(session.session_datetime_ist);
                        const end = new Date(start.getTime() + session.session_duration_minutes * 60000);
                        return end > now;
                    });

                    setLiveSession(nextSession || null);
                }
            } catch (error) {
                setLiveSession(null);
            }
        };
        fetchLiveSessions();
    }, []);

    useEffect(() => {
        let timer;
        if (liveSession && liveSession.session_datetime_ist) {
            const updateTimer = () => {
                const now = new Date();
                const sessionTime = new Date(liveSession.session_datetime_ist);
                const diff = sessionTime - now;
                if (diff > 0) {
                    if (diff <= 10 * 60 * 1000) {
                        setShowJoin(true);
                        setTimeLeft(null);
                    } else {
                        setShowJoin(false);
                        const hours = Math.floor(diff / (1000 * 60 * 60));
                        const minutes = Math.floor((diff / (1000 * 60)) % 60);
                        const seconds = Math.floor((diff / 1000) % 60);
                        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
                    }
                } else {
                    setShowJoin(true);
                    setTimeLeft(null);
                }
            };
            updateTimer();
            timer = setInterval(updateTimer, 1000);
        } else {
            setTimeLeft(null);
            setShowJoin(false);
        }
        return () => clearInterval(timer);
    }, [liveSession]);

    const handleJoinLiveClick = async () => {
        if (!liveSession) return;
        try {
            const response = await liveSessionsService.JoinLive(liveSession.id);
            if (response?.data?.data?.meetLink) {
                window.open(response.data.data.meetLink, '_blank');
            }
        } catch (error) {
            console.log('Join live error:', error?.response?.data);
            
            // Check if it's a course purchase requirement error
            if (error?.response?.data?.message?.includes('active purchase of this course')) {
                Swal.fire({
                    title: 'Course Purchase Required',
                    text: 'You need to purchase this course to join the live session',
                    icon: 'info',
                    confirmButtonText: 'View Course',
                    confirmButtonColor: '#3399cc'
                }).then(() => {
                    // Navigate to course details page - check multiple possible field names
                    const courseId = liveSession.course_id || liveSession.courseId || liveSession.course?.id;
                    if (courseId) {
                        navigate(`/coursedetailesview?id=${courseId}`);
                    } else {
                        // If no course_id, navigate to course list
                        navigate('/');
                    }
                });
                return;
            }
            
            // Check if it's an insufficient session points error
            if (error?.response?.data?.message?.includes('Insufficient session points')) {
                // Fetch addon packs and redirect to first available pack
                try {
                    const packsRes = await addonPacksService.GetAllAddonPacksForPublic();
                    const packs = packsRes?.data?.data;
                    if (Array.isArray(packs) && packs.length > 0) {
                        Swal.fire({
                            title: 'Insufficient Session Points',
                            text: 'You need to buy addon points to join the live session',
                            icon: 'warning',
                            confirmButtonText: 'Buy Addon Points',
                            confirmButtonColor: '#ff6b35'
                        }).then(() => {
                            navigate(`/addonpackdetailesview?id=${packs[0].id}`);
                        });
                    } else {
                        // If no packs available, navigate to home
                        Swal.fire({
                            title: 'No Addon Packs Available',
                            text: 'insufficient session points. No addon packs available.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#dc3545'
                        }).then(() => {
                            navigate('/');
                        });
                    }
                } catch (e) {
                    console.error('Error fetching addon packs:', e);
                    Swal.fire({
                        title: 'Error',
                        text: 'insufficient session points. Error fetching addon packs. Please try again.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#dc3545'
                    }).then(() => {
                        navigate('/');
                    });
                }
                return;
            }
            
            if (error?.response?.status === 400) {
                // Handle other 400 errors
                console.error('400 error:', error?.response?.data);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to join live session. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#dc3545'
                });
            } else {
                // Handle other errors
                console.error('Join live session error:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to join live session. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#dc3545'
                });
            }
        }
    };

    return (
        <>
            <ClientDetaileNavBar />
            {/* Live Trading Room Chat Bubble (above the main button card) */}
            {/* {liveSession && (
                <>
                    <div className="live-chat-bubble live-chat-bubble-floating">
                        <span className="live-dot"></span>
                        <span>Live Trading Room</span>
                    </div>
                   
                    <div className="live-trading-button">
                        <div className="live-chat-avatar">
                            <img src="assets1/images/about/about-12.jpg" alt="Smiling professional" />
                        </div>
                        {!showJoin && timeLeft && (
                            <div className="session-timer" style={{ textAlign: 'center', marginTop: '8px', fontWeight: 'bold', color: '#007bff' }}>
                                {timeLeft}
                            </div>
                        )}
                        {showJoin && (
                            <button className="join-button" onClick={handleJoinLiveClick}>
                                JOIN LIVE
                            </button>
                        )}
                    </div>
                </>
            )} */}
        </>
    )
}

export default ClientDetaileLayout;
